import React, { useEffect, useReducer } from 'react'
import { useQuery, useMutation } from 'react-apollo'
import PropTypes from 'prop-types'

import { useCart } from '../CartProvider/CartProvider'
import { FlagOn, FlagOff, useFlag } from '../FeatureFlag/FeatureFlag'
import { GET_CART, UPDATE_FULFILLMENT_AND_CLEAR_CART } from '../../apollo/cart/cart.graphql'
import { isISODateTimeEqual } from '../../utils/time-utils'
import { dataByTypename } from '../../utils/apollo-helpers'
import { useRestaurantStorage } from '../../utils/restaurant-storage'
import {
  FulfillmentProvider as DeliveryFulfillmentProvider,
  FulfillmentConsumer as DeliveryFulfillmentConsumer,
  useFulfillment as useDeliveryFulfillment
} from './DeliveryFulfillmentProvider'

export const FULFILLMENT_TYPES = {
  ASAP: 'ASAP',
  FUTURE: 'FUTURE'
}

const FULFILLMENT_WARNING_CODE = 'CART_UNFULFILLABLE'

// this is necessary because times locally versus times from bff
// differ slightly, so the times need to be compared in a normalized fashion
const isSameFulfillment = (a, b) =>
  a.fulfillmentType === b.fulfillmentType &&
  isISODateTimeEqual(a.fulfillmentTime, b.fulfillmentTime)

const warningsContainsFulfillment = (warnings = []) => {
  return warnings.some(warning => warning.code === FULFILLMENT_WARNING_CODE)
}

const FulfillmentContext = React.createContext({})

export const FulfillmentProviderInternal = ({ children }) => {
  const fulfillmentStorage = useRestaurantStorage()
  const { cartGuid: guid } = useCart()
  const { data, loading } = useQuery(GET_CART, { variables: { guid }, skip: !guid })
  const [updateFulfillmentAndClearCart, { error }] = useMutation(UPDATE_FULFILLMENT_AND_CLEAR_CART)
  const [fulfillmentState, setFulfillmentState] = useReducer(
    (prevState, nextState) => ({ ...nextState, selected: true }),
    {
      fulfillmentType: fulfillmentStorage.get('fulfillmentType') || FULFILLMENT_TYPES.ASAP,
      fulfillmentTime: fulfillmentStorage.get('fulfillmentTime') || null,
      valid: true,
      selected: Boolean(fulfillmentStorage.get('fulfillmentType'))
    }
  )

  // when data is loaded and there is a cart fulfillmentType set,
  // then clear localStorage and use that instead
  useEffect(() => {
    const { CartResponse } = data ? dataByTypename(data.cartV2) : {}
    if (CartResponse && CartResponse.cart.fulfillmentType) {
      const valid = !warningsContainsFulfillment(CartResponse.warnings)
      // clear local storage and use cart as canonical source
      fulfillmentStorage.remove('fulfillmentType')
      fulfillmentStorage.remove('fulfillmentTime')

      setFulfillmentState({
        fulfillmentType: CartResponse.cart.fulfillmentType,
        fulfillmentTime: CartResponse.cart.fulfillmentDateTime,
        valid
      })
    }
  }, [data, fulfillmentStorage])

  // any time the state changes and differs from cart values, updateAndClear cart
  // if there's no cart use local storage instead
  const setFulfillment = async ({ fulfillmentType, fulfillmentTime }) => {
    const { CartResponse } = data ? dataByTypename(data.cartV2) : {}
    const cart = CartResponse && CartResponse.cart

    const isASAP = fulfillmentType === FULFILLMENT_TYPES.ASAP
    const newFulfillment = {
      fulfillmentType,
      fulfillmentTime: isASAP ? null : fulfillmentTime,
      valid: true
    }

    // set state locally if no cart
    if (!cart) {
      fulfillmentStorage.set('fulfillmentType', newFulfillment.fulfillmentType)
      fulfillmentStorage.set('fulfillmentTime', newFulfillment.fulfillmentTime)
      setFulfillmentState(newFulfillment)
      return
    }

    // only update and clear if the cart value differs from the state
    const normalizedCart = { fulfillmentTime: cart.fulfillmentDateTime, fulfillmentType: cart.fulfillmentType }
    if (isSameFulfillment(normalizedCart, newFulfillment)) {
      return
    }

    const resp = await updateFulfillmentAndClearCart({
      variables: {
        input: {
          cartGuid: guid,
          cartFulfillmentInput: {
            fulfillmentType: newFulfillment.fulfillmentType,
            fulfillmentDateTime: newFulfillment.fulfillmentTime
          }
        }
      }
    })
    const { CartModificationError } = dataByTypename(resp.data.updateFulfillmentAndClearCart)
    if (CartModificationError) {
      throw new Error(CartModificationError.message)
    }
    setFulfillmentState(newFulfillment)
  }

  const context = {
    ...fulfillmentState,
    loading,
    error,
    setFulfillment
  }

  return (
    <FulfillmentContext.Provider value={context}>
      {children}
    </FulfillmentContext.Provider>
  )
}

FulfillmentProviderInternal.propTypes = {
  children: PropTypes.element.isRequired
}

export const FulfillmentConsumerInternal = ({ children }) => (
  <FulfillmentContext.Consumer>
    {context => children(context)}
  </FulfillmentContext.Consumer>
)

FulfillmentConsumerInternal.propTypes = {
  children: PropTypes.func.isRequired
}

const useFulfillmentInternal = () => React.useContext(FulfillmentContext)

export const FulfillmentProvider = (props) => (
  <React.Fragment>
    <FlagOn name='oo-v3-delivery'>
      <DeliveryFulfillmentProvider {...props} />
    </FlagOn>
    <FlagOff name='oo-v3-delivery'>
      <FulfillmentProviderInternal {...props} />
    </FlagOff>
  </React.Fragment>
)

export const FulfillmentConsumer = (props) => (
  <React.Fragment>
    <FlagOn name='oo-v3-delivery'>
      <DeliveryFulfillmentConsumer {...props} />
    </FlagOn>
    <FlagOff name='oo-v3-delivery'>
      <FulfillmentConsumerInternal {...props} />
    </FlagOff>
  </React.Fragment>
)

export const useFulfillment = () => {
  const enabled = useFlag('oo-v3-delivery', false)
  const useDelivery = useDeliveryFulfillment()
  const useDefault = useFulfillmentInternal()
  return enabled ? useDelivery : useDefault
}
